/**
 * Verification Context
 * Manages global verification state to prevent duplicate processing
 */

import {
  createContext,
  useState,
  useCallback,
  useMemo,
  useRef,
} from 'react';
import type {
  VerificationContextType,
  VerificationProviderProps,
} from '../types/contexts';

const VerificationContext = createContext<VerificationContextType | null>(null);

export { VerificationContext };

export function VerificationProvider({ children }: VerificationProviderProps) {
  const [isVerificationInProgress, setIsVerificationInProgress] =
    useState(false);

  // Use ref to avoid recreation of callback when state changes
  const isVerificationInProgressRef = useRef(isVerificationInProgress);
  isVerificationInProgressRef.current = isVerificationInProgress;

  /**
   * Start verification process
   * Returns true if verification was started, false if already in progress
   */
  const startVerification = useCallback((): boolean => {
    if (isVerificationInProgressRef.current) {
      return false;
    }

    setIsVerificationInProgress(true);
    return true;
  }, []);

  /**
   * End verification process
   */
  const endVerification = useCallback(() => {
    setIsVerificationInProgress(false);
  }, []);

  /**
   * Reset verification state
   */
  const resetVerification = useCallback(() => {
    setIsVerificationInProgress(false);
  }, []);

  // Memoize context value to prevent unnecessary re-renders
  const value: VerificationContextType = useMemo(
    () => ({
      isVerificationInProgress,
      startVerification,
      endVerification,
      resetVerification,
    }),
    [
      isVerificationInProgress,
      startVerification,
      endVerification,
      resetVerification,
    ]
  );

  return (
    <VerificationContext.Provider value={value}>
      {children}
    </VerificationContext.Provider>
  );
}
